import { BillingPlan as WebServiceBillingPlan, DimensionLimit, Plan, PlanPackage as WebPlanPackage, PlanPackageCriterion, PlanPackageUpdate, PlanTier, PlanUpdate } from 'fs-api-client';
import { Providers } from 'portal-shared-lib';

export const ANY_STORAGE_PROVIDER = 'Any';
export const ANY_STORAGE_OWNER = 'any';

export interface BillingPlanState {
    billingPlan: BillingPlan;
    hasChanges: boolean;
}

export enum TierProp {
    From = 'From',
    UpTo = 'UpTo',
    FlatPrice = 'FlatPrice',
    PricePerUnit = 'PricePerUnit'
}

export class BillingPlan {
    id: string;
    name: string;
    planPackages: Array<PlanPackage>;
    isCustomPricing: boolean;
}

export const CUSTOM_LITE_TYPE = 'custom_lite';

export class PlanPackage extends WebPlanPackage {
    isLite: boolean;
}

export class BillingPlanModel {
    private workingCopy: BillingPlan;
    private billingPlan: BillingPlan;
    private checkPoint: BillingPlan | null;
    private planPackIdGen: number;
    private static NEW_EXTERNAL_ID = 'NEW_BILLING_PLAN';

    constructor(
        webBillingPlan: WebServiceBillingPlan,
        public onPlanUpdated: (billingPlanState: BillingPlanState) => void
    ) {
        if (webBillingPlan) {
            this.billingPlan = this.toBillingPlan(webBillingPlan);
            this.workingCopy = JSON.parse(JSON.stringify(this.billingPlan));
        }

        this.planPackIdGen = 1;
    }

    createCheckPoint() {
        this.checkPoint = this.copyPlan(this.workingCopy);
    }

    restoreCheckPoint() {
        if (this.checkPoint) {
            this.workingCopy = this.copyPlan(this.checkPoint);
        }

        this.notifyPlanUpdated();
    }

    removeCheckPoint() {
        this.checkPoint = null;
    }

    applyUpdates() {
        this.billingPlan = this.copyPlan(this.workingCopy);
    }

    addCriterion(planPackId: string, subscriptionType: PlanPackageCriterion.SubscriptionTypeEnum): void {
        const planPack = this.workingCopy.planPackages.find(planPackage => planPackage.id === planPackId);
        if (!planPack) {
            return;
        }

        const criterion = this.getCriterionForSubscriptionType(subscriptionType);
        if (!criterion) {
            return;
        }

        planPack.includeCriteria.push(criterion);
        this.notifyPlanUpdated();
    }

    getCriterionForSubscriptionType(
        subscriptionType: PlanPackageCriterion.SubscriptionTypeEnum
    ): PlanPackageCriterion | null {
        const splittedType = subscriptionType.toString().split('_');
        const parsedSubType = splittedType[0] as unknown as PlanPackageCriterion.SubscriptionTypeEnum;
        switch (parsedSubType as unknown as PlanPackageCriterion.SubscriptionTypeEnum | string) {
            case PlanPackageCriterion.SubscriptionTypeEnum.Advanced:
                return {
                    storageOwner: PlanPackageCriterion.StorageOwnerEnum.Lucidlink,
                    storageProvider: splittedType[1] === 'aws' ? Providers.AWS : Providers.IBM,
                    subscriptionType: parsedSubType
                };
            case PlanPackageCriterion.SubscriptionTypeEnum.Enterprise:
            case PlanPackageCriterion.SubscriptionTypeEnum.Teams:
                return {
                    storageOwner: PlanPackageCriterion.StorageOwnerEnum.Lucidlink,
                    storageProvider: Providers.IBM,
                    subscriptionType: parsedSubType
                };
            case PlanPackageCriterion.SubscriptionTypeEnum.Basic:
                return {
                    storageOwner: PlanPackageCriterion.StorageOwnerEnum.Lucidlink,
                    storageProvider: Providers.Wasabi,
                    subscriptionType: parsedSubType
                };
            case PlanPackageCriterion.SubscriptionTypeEnum.Custom:
            case CUSTOM_LITE_TYPE:
                return {
                    storageOwner: PlanPackageCriterion.StorageOwnerEnum.Customer,
                    storageProvider: ANY_STORAGE_PROVIDER,
                    subscriptionType: parsedSubType
                };
            case PlanPackageCriterion.SubscriptionTypeEnum.Any:
                return {
                    storageOwner: PlanPackageCriterion.StorageOwnerEnum.Any,
                    storageProvider: ANY_STORAGE_PROVIDER,
                    subscriptionType: parsedSubType
                };
            default:
                return null;
        }
    }

    removeCriterion(planPackId: string, criterionIdx: number): void {
        const planPack = this.workingCopy.planPackages.find(planPackage => planPackage.id === planPackId);
        if (!planPack) {
            return;
        }

        if (criterionIdx > planPack.includeCriteria.length) {
            return;
        }

        planPack.includeCriteria.splice(criterionIdx, 1);
        this.notifyPlanUpdated();
    }

    getBillingPlan(): WebServiceBillingPlan {
        return this.copyPlan(this.workingCopy);
    }

    resetBillingPlan(newBillingPlan: WebServiceBillingPlan): void {
        this.billingPlan = this.toBillingPlan(newBillingPlan);
        this.workingCopy = this.copyPlan(this.billingPlan);
        this.planPackIdGen = 1;
    }

    addNewPlanPackage(): void {
        const workingCopy = this.workingCopy;
        const newPlanPackage = {
            isLite: false,
            dimensionLimits: [],
            excludeCriteria: [],
            id: (this.planPackIdGen++).toString(),
            includeCriteria: [{
                storageOwner: ANY_STORAGE_OWNER as any,
                storageProvider: ANY_STORAGE_PROVIDER,
                subscriptionType: PlanPackageCriterion.SubscriptionTypeEnum.Any
            }],
            plans: [{
                dimension: Plan.DimensionEnum.ManagedCapacity,
                externalId: BillingPlanModel.NEW_EXTERNAL_ID,
                tiers: [{
                    flatPrice: '0',
                    from: 1,
                    pricePerUnit: '0'
                }]
            }]
        } as PlanPackage;
        workingCopy.planPackages.unshift(newPlanPackage);
        this.syncLitePackages();

        this.notifyPlanUpdated();
    }

    removePlanPackage(planPackId: string): void {
        const billingPlan = this.workingCopy;
        const planPackIdx = billingPlan.planPackages.findIndex(planPack => planPack.id === planPackId);
        if (planPackIdx === -1) {
            return;
        }

        billingPlan.planPackages.splice(planPackIdx, 1);
        this.syncLitePackages();
        this.notifyPlanUpdated();
    }

    removePlanFormPlanPackage(planPackId: string, dimension: Plan.DimensionEnum): void {
        const billingPlan = this.workingCopy;
        const planPackIdx = billingPlan.planPackages.findIndex(newPlanPack => newPlanPack.id === planPackId);
        if (planPackIdx === -1) {
            return;
        }

        const planPack = billingPlan.planPackages[planPackIdx];
        const planIdx = planPack.plans.findIndex(plan => plan.dimension === dimension);

        if (planIdx === -1) {
            return;
        }

        planPack.plans.splice(planIdx, 1);

        if (planPack.plans.length === 0) {
            billingPlan.planPackages.splice(planPackIdx, 1);
        }
        this.syncLitePackages();
        this.notifyPlanUpdated();
    }

    changePlanPackStorageOwner(planPackId: string, criterionIdx: number, storageOwner: string): void {
        const billingPlanCopy = this.workingCopy;
        const planPack = billingPlanCopy.planPackages.find(planPackage => planPackage.id === planPackId)!;
        planPack.includeCriteria[criterionIdx].storageOwner = storageOwner as any;

        this.notifyPlanUpdated();
    }

    changePlanPackStorageProvider(planPackId: string, criterionIdx: number, storageProvider: string): void {
        const billingPlanCopy = this.workingCopy;
        const planPack = billingPlanCopy.planPackages.find(planPackage => planPackage.id === planPackId)!;
        planPack.includeCriteria[criterionIdx].storageProvider = storageProvider;

        this.notifyPlanUpdated();
    }

    changePlanPackSubscriptionType(planPackId: string, criterionIdx: number, subscriptionType: string): void {
        const billingPlanCopy = this.workingCopy;
        const planPack = billingPlanCopy.planPackages.find(planPackage => planPackage.id === planPackId)!;

        if (!this.validateSubscriptionTypeChange(subscriptionType)) {
            return;
        }

        const criterion = this.getCriterionForSubscriptionType(subscriptionType as any);
        if (!criterion) {
            return;
        }

        planPack.includeCriteria[criterionIdx] = criterion;

        const isLite = subscriptionType === CUSTOM_LITE_TYPE;

        if ((planPack.isLite && !isLite) || (!planPack.isLite && isLite)) {
            planPack.includeCriteria = [criterion];
        }

        planPack.isLite = isLite;

        if (isLite) {
            planPack.includeCriteria[0].storageProvider = Providers.Wasabi;
            planPack.includeCriteria.push({...planPack.includeCriteria[0], storageProvider: Providers.Backblaze});
        } else if (!isLite && subscriptionType as any === PlanPackageCriterion.SubscriptionTypeEnum.Custom) {
            planPack.includeCriteria[0].storageProvider = ANY_STORAGE_PROVIDER;
        }

        this.syncLitePackages();
        this.notifyPlanUpdated();
    }

    isLite(planPackId: string): boolean {
        const planPack = this.workingCopy.planPackages.find(planPackage => planPackage.id === planPackId);
        return !!planPack && planPack.isLite;
    }

    addProvider(planPackId: string): void {
        const planPack = this.workingCopy.planPackages.find(currentplanPack => currentplanPack.id === planPackId);
        if (!planPack || !planPack.isLite || planPack.includeCriteria[0].subscriptionType !== PlanPackageCriterion.SubscriptionTypeEnum.Custom) {
            return;
        }

        if (planPack.includeCriteria.length >= 2) {
            return;
        }

        if (planPack.includeCriteria[0].storageProvider === Providers.Wasabi) {
            planPack.includeCriteria.push({
                storageOwner: PlanPackageCriterion.StorageOwnerEnum.Customer,
                storageProvider: Providers.Backblaze,
                subscriptionType: PlanPackageCriterion.SubscriptionTypeEnum.Custom
            });
        } else {
            planPack.includeCriteria.push({
                storageOwner: PlanPackageCriterion.StorageOwnerEnum.Customer,
                storageProvider: Providers.Wasabi,
                subscriptionType: PlanPackageCriterion.SubscriptionTypeEnum.Custom
            });
        }

        this.syncLitePackages();
        this.notifyPlanUpdated();
    }

    removeProvider(planPackId: string, criterionIdx: number): void {
        const planPack = this.workingCopy.planPackages.find(currentplanPack => currentplanPack.id === planPackId);
        if (!planPack || !planPack.isLite || planPack.includeCriteria[0].subscriptionType !== PlanPackageCriterion.SubscriptionTypeEnum.Custom) {
            return;
        }

        if (planPack.includeCriteria.length <= 1) {
            return;
        }

        planPack.includeCriteria.splice(criterionIdx, 1);
        this.syncLitePackages();
        this.notifyPlanUpdated();
    }

     changeProvider(planPackId: string, _criterionIdx: number, newProvider: Providers): void {
        const planPack = this.workingCopy.planPackages.find(currentplanPack => currentplanPack.id === planPackId);
        if (!planPack || planPack.includeCriteria[0].subscriptionType !== PlanPackageCriterion.SubscriptionTypeEnum.Custom) {
            return;
        }

        if (newProvider !== Providers.Wasabi && newProvider !== Providers.Backblaze) {
            return;
        }

        if (planPack.includeCriteria.length >= 2) {
            return;
        }

        planPack.includeCriteria[0].storageProvider = newProvider;
        this.syncLitePackages();
        this.notifyPlanUpdated();
    }

    modifyPlanDimension(
        oldDimension: Plan.DimensionEnum,
        newDimension: Plan.DimensionEnum,
        planPackId: string
    ): void {
        const planPack = this.getPlanPack(planPackId);
        if (!planPack) {
            return;
        }

        const plan = planPack.plans.find(planToBeFound => planToBeFound.dimension === oldDimension);
        if (!plan) {
            return;
        }

        plan.dimension = newDimension;
        this.notifyPlanUpdated();
    }

    modifyPlanTier(
        newValue: string,
        planPackId: string,
        planDimension: Plan.DimensionEnum,
        tierIdx: number,
        tierProp: TierProp
    ) {
        const planPackage = this.getPlanPack(planPackId)!;
        const plan = planPackage.plans.find(planToBeFound => planToBeFound.dimension === planDimension);
        if (!plan) {
            return;
        }
        const isLastTier = plan.tiers.length - 1 === tierIdx;
        const isFirstTier = tierIdx === 0;

        const tier = plan.tiers[tierIdx];

        if (tierProp === TierProp.From) {
            const parsedValue = parseInt(newValue, 10);
            if (Number.isNaN(parsedValue)) {
                tier.from = 0;
                return;
            }
            if (tier.upTo && parsedValue >= tier.upTo) {
                tier.from = tier.upTo - 1;
            } else {
                tier.from = parsedValue;
            }

            if (!isFirstTier) {
                plan.tiers[tierIdx - 1].upTo = parsedValue - 1;
            }

        } else if (tierProp === TierProp.UpTo && !isLastTier) {
            const parsedValue = parseInt(newValue, 10);
            if (Number.isNaN(parsedValue)) {
                tier.upTo = 0;
                return;
            }
            if (parsedValue <= tier.from) {
                tier.upTo = tier.from + 1;
            } else {
                tier.upTo = parsedValue;
            }

            if (!isLastTier) {
                plan.tiers[tierIdx + 1].from = parsedValue + 1;
            }

        } else if (tierProp === TierProp.PricePerUnit) {
            tier.pricePerUnit = newValue;
        } else if (tierProp === TierProp.FlatPrice) {
            tier.flatPrice = newValue;
        }
        this.notifyPlanUpdated();
    }

    addPlanTier(planPackId: string, planDimension: Plan.DimensionEnum): void {
        const planPack = this.getPlanPack(planPackId);
        if (!planPack) {
            return;
        }
        const plan = planPack.plans.find(planToBeFound => planToBeFound.dimension === planDimension);
        if (!plan) {
            return;
        }
        const lastTierIdx = plan.tiers.length - 1;
        const lastTierFrom = plan.tiers[lastTierIdx].from;
        plan.tiers[lastTierIdx].upTo = lastTierFrom + 1;
        plan.tiers.push({
            from: lastTierFrom + 2,
            flatPrice: '0',
            pricePerUnit: '0'
        });
        this.notifyPlanUpdated();
    }

    removePlanTier(planPackId: string, planDimension: Plan.DimensionEnum, planTierIdx: number): void {
        const planPack = this.getPlanPack(planPackId);
        if (!planPack) {
            return;
        }
        const plan = planPack.plans.find(planToBeFound => planToBeFound.dimension === planDimension);
        if (!plan) {
            return;
        }

        if (plan.tiers.length === 1) {
            return;
        }

        const lastTierIdx = plan.tiers.length - 1;
        if (planTierIdx === lastTierIdx) {
            delete plan.tiers[planTierIdx - 1].upTo;
        } else {
            plan.tiers[planTierIdx + 1].from = plan.tiers[planTierIdx].from;
        }
        plan.tiers.splice(planTierIdx, 1);
        this.notifyPlanUpdated();
    }

    getBillingUpdates(): PlanPackageUpdate[] {
        const planPacks = this.workingCopy.planPackages;
        let result: PlanPackageUpdate[] = [];

        for (const planPack of planPacks) {
            const originalPlanPack = this.billingPlan.planPackages.find(
                p => p.id === planPack.id);

            if (!originalPlanPack) {
                result.push(this.addCreateBillingPlanUpdate(planPack));
                continue;
            }

            if (!this.planPackagesEqual(originalPlanPack, planPack)) {
                result = result.concat(this.addUpdateBillingPlanUpdate(originalPlanPack, planPack));
            }
        }

        for (const originalPlanPack of this.billingPlan.planPackages) {
            const planPack = this.workingCopy.planPackages.find(
                p => p.id === originalPlanPack.id);

            if (!planPack) {
                result.push(this.addDeleteBillingPlanUpdate(originalPlanPack));
            }
        }
        return result;
    }

    undoChanges() {
        this.workingCopy = this.copyPlan(this.billingPlan);
        this.notifyPlanUpdated();
    }

    hasChanges() {
        const workingPlanPacks = this.workingCopy.planPackages;
        const originalPlanPacks = this.billingPlan.planPackages;

        return !this.arraysEuqual(originalPlanPacks, workingPlanPacks, (
            (planPack1: PlanPackage, planPack2: PlanPackage) => {
                if (planPack1.id === planPack2.id) {
                    return this.planPackagesEqual(planPack1, planPack2);
                }
                return false;
            }
        ));
    }

    private addCreateBillingPlanUpdate(planPack: PlanPackage): PlanPackageUpdate {
        const plan = planPack.plans[0];
        const result: PlanPackageUpdate = {
            action: PlanPackageUpdate.ActionEnum.Create,
            includeCriteriaUpdate: planPack.includeCriteria,
            planUpdate: {...planPack.plans[0], action: PlanUpdate.ActionEnum.Create}
        };

        if (plan.tiers.length === 1) {
            result.planUpdate!.price = plan.tiers[0].pricePerUnit;
        } else {
            result.planUpdate!.tieredPlan = plan.tiers;
        }

        return result;
    }

    private addUpdateBillingPlanUpdate(originalPlanPack: PlanPackage, planPack: PlanPackage): PlanPackageUpdate[] {
        let result: PlanPackageUpdate[] = [];

        // TODO Criteria update
        if (!this.planPackageCriteriaEqual(originalPlanPack.includeCriteria, planPack.includeCriteria)) {
            result.push({
                action: PlanPackageUpdate.ActionEnum.Update,
                id: originalPlanPack.id,
                includeCriteriaUpdate: planPack.includeCriteria
            });
        }

        if (!this.planPackageCriteriaEqual(originalPlanPack.excludeCriteria, planPack.excludeCriteria)) {
            result.push({
                action: PlanPackageUpdate.ActionEnum.Update,
                id: originalPlanPack.id,
                excludeCriteriaUpdate: planPack.excludeCriteria
            });
            return result;
        }

        for (const originalPlan of originalPlanPack.plans) {
            const existing = planPack.plans.find(plan => plan.externalId === originalPlan.externalId);
            let currentPlanUpdate: PlanPackageUpdate = {
                action: PlanPackageUpdate.ActionEnum.Update,
                id: originalPlanPack.id,
            };
            if (!existing) {
                currentPlanUpdate.planUpdate = {
                    action: PlanUpdate.ActionEnum.Delete,
                    externalId: originalPlan.externalId
                };
                result.push(currentPlanUpdate);
                continue;
            }

            if (!this.plansEqual(originalPlan, existing)) {
                if (existing.tiers.length === 1) {
                    currentPlanUpdate.planUpdate = {
                        dimension: existing.dimension,
                        externalId: originalPlan.externalId,
                        price: existing.tiers[0].pricePerUnit,
                        action: PlanUpdate.ActionEnum.Update
                    };
                } else {
                    currentPlanUpdate.planUpdate = {
                        dimension: existing.dimension,
                        externalId: originalPlan.externalId,
                        tieredPlan: existing.tiers,
                        action: PlanUpdate.ActionEnum.Update
                    };
                }
                result.push(currentPlanUpdate);
            }
        }
        return result;
    }

    private addDeleteBillingPlanUpdate(planPack: PlanPackage): PlanPackageUpdate {
        return {
            id: planPack.id,
            action: PlanPackageUpdate.ActionEnum.Delete
        };
    }

    private notifyPlanUpdated() {
        this.onPlanUpdated({
            billingPlan: this.copyPlan(this.workingCopy),
            hasChanges: this.hasChanges()
        });
    }

    private copyPlan(billingPlan: BillingPlan): BillingPlan {
        return JSON.parse(JSON.stringify(billingPlan));
    }

    private getPlanPack(planPackId: string): PlanPackage | undefined {
        return this.workingCopy.planPackages.
            find(planPack => planPack.id === planPackId);
    }

    private arraysEuqual<T>(array1: Array<T>, array2: Array<T>, comparator: any): boolean {
        if (array1.length !== array2.length) {
            return false;
        }

        for (const el1 of array1) {
            if (!array2.find(el2 => comparator(el1, el2))) {
                return false;
            }
        }
        return true;
    }

    private planPackagesEqual(planPack1: PlanPackage, planPack2: PlanPackage): boolean {
        return this.planPackageCriteriaEqual(planPack1.includeCriteria, planPack2.includeCriteria) &&
            this.planPackageCriteriaEqual(planPack1.excludeCriteria, planPack2.excludeCriteria) &&
            this.planPackageDimensionLimitsEqual(planPack1.dimensionLimits, planPack2.dimensionLimits) &&
            this.planPackagePlansEqual(planPack1.plans, planPack2.plans);
    }

    private planPackagePlansEqual(plans1: Plan[], plans2: Plan[]): boolean {
        return this.arraysEuqual(plans1, plans2, (plan1: Plan, plan2: Plan) => {
            return this.plansEqual(plan1, plan2);
        });
    }

    private plansEqual(plan1: Plan, plan2: Plan) {
        return plan2.dimension === plan1.dimension &&
            plan2.externalId === plan1.externalId &&
            this.arraysEuqual(plan1.tiers, plan2.tiers, (tier1: PlanTier, tier2: PlanTier) => {
                    return tier1.flatPrice === tier2.flatPrice &&
                        tier1.pricePerUnit === tier2.pricePerUnit &&
                        tier1.from === tier2.from &&
                        tier1.upTo === tier2.upTo;
                });
    }

    private planPackageDimensionLimitsEqual(
        dimLimits1: DimensionLimit[],
        dimLimits2: DimensionLimit[]
    ): boolean {
        return this.arraysEuqual(dimLimits1, dimLimits2,
                                 (dim1: DimensionLimit, dim2: DimensionLimit) => {
                                     return dim1.dimension === dim2.dimension && dim1.minReportValue === dim2.minReportValue;
                                 }
        );
    }

    private planPackageCriteriaEqual(
        criteria1: PlanPackageCriterion[],
        criteria2: PlanPackageCriterion[]
    ): boolean {
        return this.arraysEuqual(criteria1, criteria2,
                                 (criterion1: PlanPackageCriterion, criterion2: PlanPackageCriterion) => {
                                     return criterion1.storageOwner === criterion2.storageOwner &&
                                         criterion1.storageProvider.toLowerCase() === criterion2.storageProvider.toLowerCase() &&
                                         criterion1.subscriptionType === criterion2.subscriptionType;
                                 }
        );
    }

    private toBillingPlan(webserviceBillingPlan: WebServiceBillingPlan): BillingPlan {
        let result: BillingPlan = {
            id: webserviceBillingPlan.id,
            isCustomPricing: webserviceBillingPlan.isCustomPricing,
            name: webserviceBillingPlan.name,
            planPackages: this.toPlanPackages(webserviceBillingPlan.planPackages)
        };

        return result;
    }

    private toPlanPackages(planPackages: WebPlanPackage[]): PlanPackage[] {
        return planPackages.map(planPackage => this.toPlanPackage(planPackage));
    }

    private toPlanPackage(planPackage: WebPlanPackage): PlanPackage {
        let result: PlanPackage = {
            dimensionLimits: planPackage.dimensionLimits,
            id: planPackage.id,
            plans: planPackage.plans,
            excludeCriteria: planPackage.excludeCriteria,
            includeCriteria: planPackage.includeCriteria,
            isLite: this.deduceLitePackage(planPackage)
        };
        return result;
    }

    private deduceLitePackage(planPackage: WebPlanPackage): boolean {
        if (planPackage.includeCriteria[0].subscriptionType !== PlanPackageCriterion.SubscriptionTypeEnum.Custom) {
            return false;
        }

        return planPackage.includeCriteria[0].storageProvider !== 'Any';
    }

    private syncLitePackages() {
        const planPackages = this.workingCopy.planPackages;
        const litePack = planPackages.find(planPack => planPack.isLite === true);

        const customFilter = (planPack: PlanPackage) => {
            if (planPack.includeCriteria.length > 1) {
                return false;
            }

            const criterion = planPack.includeCriteria[0];
            if (criterion.subscriptionType !== PlanPackageCriterion.SubscriptionTypeEnum.Custom ||
                criterion.storageProvider !== ANY_STORAGE_PROVIDER
            ) {
                return false;
            }

            return true;
        };

        const customPackages = planPackages.filter(customFilter);
        for (const customPack of customPackages) {
            customPack.excludeCriteria = [];
            if (litePack) {
                for (const liteCriterion of litePack.includeCriteria) {
                    customPack.excludeCriteria.push({
                        storageOwner: liteCriterion.storageOwner,
                        storageProvider: liteCriterion.storageProvider,
                        subscriptionType: liteCriterion.subscriptionType
                    });
                }
            }
        }
    }

    private validateSubscriptionTypeChange(subscriptionType: string) {
        if (subscriptionType !== CUSTOM_LITE_TYPE) {
            return true;
        }

        const litePacks = this.workingCopy.planPackages.find(planPack => planPack.isLite);
        return !litePacks;
    }

}


// WEBPACK FOOTER //
// ./src/backoffice/components/billing/BillingPlanModel.ts